/**
 * @module features/client-device-management/components/BulkCsvModal
 * @description Responsive modal (mobile bottom sheet / desktop dialog) for bulk
 * CSV device assignment to `{Org}` with template download and line-item diagnostics.
 */
import * as React from "react";
import {
  AlertCircle,
  CheckCircle2,
  Download,
  FileSpreadsheet,
  Loader2,
  TriangleAlert,
  Upload,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AppError } from "@/lib/api-client";
import { BulkCsvAssignResponse } from "../types";
import {
  downloadAssignmentCsvTemplate,
  useBulkCsvAssign,
} from "../api/assignmentApi";

export interface BulkCsvModalProps {
  open: boolean;
  onClose: () => void;
  clientId: string;
  orgName: string;
}

export function BulkCsvModal({
  open,
  onClose,
  clientId,
  orgName,
}: BulkCsvModalProps) {
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
  const [isDownloadingTemplate, setIsDownloadingTemplate] = React.useState(false);
  const [errorState, setErrorState] = React.useState<AppError | null>(null);
  const [report, setReport] = React.useState<BulkCsvAssignResponse | null>(null);

  const bulkCsvMutation = useBulkCsvAssign(clientId);

  const handleClose = (): void => {
    setSelectedFile(null);
    setErrorState(null);
    setReport(null);
    onClose();
  };

  const handleTemplateDownload = async (): Promise<void> => {
    setIsDownloadingTemplate(true);
    setErrorState(null);
    try {
      await downloadAssignmentCsvTemplate();
    } catch (rawErr: unknown) {
      setErrorState(
        AppError.fromUnknown(rawErr, "Unable to download CSV template.")
      );
    } finally {
      setIsDownloadingTemplate(false);
    }
  };

  const handleUploadSubmit = async (): Promise<void> => {
    if (!selectedFile) return;
    setErrorState(null);
    setReport(null);
    try {
      const result = await bulkCsvMutation.mutateAsync(selectedFile);
      setReport(result);
      setSelectedFile(null);
    } catch (rawErr: unknown) {
      setErrorState(
        AppError.fromUnknown(rawErr, "Failed to process uploaded CSV file.")
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent sheetOnMobile className="sm:max-w-lg max-h-[90dvh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Bulk CSV Assignment for {orgName}</DialogTitle>
          <DialogDescription>
            Upload a CSV mapping MAC addresses or registration IDs to custom names
            and deployment locations.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 overflow-y-auto min-h-0 pr-0.5">
          {errorState && (
            <div
              role="alert"
              className="flex items-start gap-2 rounded-xl border border-danger/20 bg-danger-soft p-3 text-xs text-danger"
            >
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" aria-hidden="true" />
              <div className="min-w-0">
                <p>{errorState.errorDetails}</p>
                {errorState.requestId && (
                  <p className="mt-1 font-mono text-[11px] text-muted-foreground break-all">
                    Request ID: {errorState.requestId}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Step 1: Download Template */}
          <div className="rounded-xl border border-border/80 bg-muted/20 p-3.5 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-xs font-medium text-foreground">
                1. Download CSV Template
              </p>
              <p className="text-[11px] text-muted-foreground font-mono truncate">
                device_identifier, custom_device_name, location
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={isDownloadingTemplate}
              onClick={() => void handleTemplateDownload()}
              className="h-9 touch:min-h-[44px] text-xs gap-1.5 shrink-0"
            >
              {isDownloadingTemplate ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
              ) : (
                <Download className="h-3.5 w-3.5" aria-hidden="true" />
              )}
              <span>Template</span>
            </Button>
          </div>

          {/* Step 2: Select CSV File */}
          <div className="space-y-1.5">
            <label
              htmlFor="bulk-assign-csv-file"
              className="text-xs font-medium text-foreground block"
            >
              2. Upload Completed CSV File
            </label>
            <Input
              id="bulk-assign-csv-file"
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => {
                const file = e.target.files?.[0] ?? null;
                setSelectedFile(file);
                setReport(null);
              }}
              className="h-11 sm:h-10 text-xs bg-card cursor-pointer pt-2"
            />
            {selectedFile && (
              <p className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                <FileSpreadsheet className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                <span className="truncate">{selectedFile.name}</span>
              </p>
            )}
          </div>

          {/* Diagnostics Report */}
          {report && (
            <div
              role="status"
              className={`rounded-xl border p-3.5 text-xs space-y-2 ${
                report.failed_assignments > 0
                  ? "border-warning/30 bg-warning-soft text-foreground"
                  : "border-success/30 bg-success-soft text-success"
              }`}
            >
              <div className="flex items-center gap-2 font-medium">
                {report.failed_assignments > 0 ? (
                  <TriangleAlert
                    className="h-4 w-4 text-warning shrink-0"
                    aria-hidden="true"
                  />
                ) : (
                  <CheckCircle2
                    className="h-4 w-4 text-success shrink-0"
                    aria-hidden="true"
                  />
                )}
                <span>
                  Processed {report.total_rows} rows:{" "}
                  {report.successful_assignments} assigned,{" "}
                  {report.failed_assignments} failed.
                </span>
              </div>

              {report.errors.length > 0 && (
                <div className="max-h-40 overflow-y-auto rounded-lg border border-border/70 bg-card p-2.5 space-y-1.5 text-[11px]">
                  {report.errors.map((err, idx) => (
                    <div
                      key={`${err.line}-${idx}`}
                      className="flex items-start gap-2 text-foreground"
                    >
                      <span className="font-mono text-danger shrink-0">
                        Line {err.line}:
                      </span>
                      <span className="min-w-0">
                        <span className="font-mono font-medium">
                          {err.identifier}
                        </span>{" "}
                        — {err.error_message}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={bulkCsvMutation.isPending}
            className="h-10 sm:h-9 touch:min-h-[44px] text-xs"
          >
            Close
          </Button>
          <Button
            type="button"
            disabled={!selectedFile || bulkCsvMutation.isPending}
            onClick={() => void handleUploadSubmit()}
            className="h-10 sm:h-9 touch:min-h-[44px] text-xs gap-1.5"
          >
            {bulkCsvMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                <span>Processing CSV...</span>
              </>
            ) : (
              <>
                <Upload className="h-4 w-4" aria-hidden="true" />
                <span>Upload &amp; Assign</span>
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

